import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shopsQueryOptions } from '@queries/shops';
import { ShopType } from '@schema/cart';
import type {
  GetShopsQuery,
  ShopFragmentMainEndpointFragment
} from '@schema/main';

interface UseShopsOptions {
  isOnlyBars?: boolean;
  isOnlyShops?: boolean;
}

type ShopImage = NonNullable<GetShopsQuery['getShops']>[0] & {
  shopTitle?: string | null;
};

interface UseShopsReturn {
  shops: ShopFragmentMainEndpointFragment[];
  isShopsLoading: boolean;
  oysterBars: Array<{
    value: number;
    label: string;
    address?: string | null;
    opensSoon: boolean;
  }>;
  shopsData: any[];
  imagesData: ShopImage[];
  isLoading: boolean;
}

/**
 * ЭТАП 2 (SSR) — хук переписан на react-query. Публичный контракт (набор
 * полей и их смысл) сохранён 1:1, менялась только внутренняя механика.
 *
 * Что было и почему не годилось:
 *  1. Данные жили в СТЕЙТЕ и заполнялись `useEffect` — на сервере эффекты не
 *     выполняются, поэтому все блоки на хуке (OurAddresses на /store и
 *     лендингах, BookTable, EasyAtmosphere) приезжали в SSR пустыми.
 *  2. Рядом лежал МОДУЛЬНЫЙ кэш (`shopsCache`, `shopsCachePromise`). На
 *     сервере модуль общий для всех запросов — ровно тот класс утечки
 *     между параллельными SSR-запросами, из-за которого в своё время
 *     пришлось заводить AsyncLocalStorage.
 *  3. Использовался СИНГЛТОН `queryClient` из провайдера напрямую, а не
 *     per-request клиент: данные писались мимо того кэша, который уезжает
 *     в дегидрацию.
 *
 * `useQuery` решает всё три разом: клиент берётся из контекста (на сервере —
 * per-request), кэш дедуплицирует параллельные обращения сам, а данные
 * доступны уже в первом рендере.
 */

/**
 * Модульный select: ссылка стабильна, поэтому react-query не пересчитывает
 * результат на каждый рендер.
 */
const selectShops = (
  res: GetShopsQuery
): { shops: ShopFragmentMainEndpointFragment[]; images: ShopImage[] } => {
  const allShops = (res.getShops ?? []) as ShopFragmentMainEndpointFragment[];

  const images = allShops
    .map((shop: any) => {
      const bestAttachment =
        shop.attachments?.length > 0
          ? shop.attachments.reduce((max: any, current: any) =>
              (current.weight || 0) > (max.weight || 0) ? current : max
            )
          : null;

      return {
        id: shop.id,
        attachmentsItems: bestAttachment
          ? [
              {
                description:
                  bestAttachment.description ||
                  shop.title ||
                  'Фото изнутри наших баров',
                attachmentsItem: {
                  tempFullFilePath:
                    bestAttachment.attachmentsItem?.tempFullFilePath
                }
              }
            ]
          : [],
        shopTitle: shop.title ?? null
      };
    })
    .filter(item => item.attachmentsItems.length > 0);

  return { shops: allShops, images: images as unknown as ShopImage[] };
};

export const useShops = ({
  isOnlyBars,
  isOnlyShops
}: UseShopsOptions = {}): UseShopsReturn => {
  // getShops — это main-endpoint запрос, его магазины имеют «главную» форму
  // (без `tips`). Потребители работают с ними как с cart-фрагментом, но
  // `tips` не читают — приведение сохраняет прежний публичный контракт.
  const { data, isPending } = useQuery({
    ...shopsQueryOptions(),
    select: selectShops
  });

  const shops = data?.shops;

  const filteredShops = useMemo(
    () =>
      (shops ?? []).filter(item => {
        if (isOnlyBars) {
          return item.type === ShopType.OysterBar;
        }
        if (isOnlyShops) {
          return item.type === ShopType.Shop;
        }
        return true;
      }),
    [shops, isOnlyBars, isOnlyShops]
  );

  const oysterBars = useMemo(
    () =>
      (shops ?? [])
        .filter(
          shop => shop.type === ShopType.OysterBar && shop.id && shop.title
        )
        .map(shop => ({
          value: shop.id as number,
          label: shop.title as string,
          address: shop.address ?? null,
          opensSoon: !!shop.opens_soon
        })),
    [shops]
  );

  return {
    shops: filteredShops,
    isShopsLoading: isPending,
    oysterBars,
    // shopsData исторически = тот же список без фильтра по типу
    shopsData: shops ?? [],
    imagesData: data?.images ?? [],
    isLoading: isPending
  };
};

export const useShops2 = (): Pick<
  UseShopsReturn,
  'shopsData' | 'imagesData' | 'isLoading'
> => {
  const { shopsData, imagesData, isLoading } = useShops();
  return { shopsData, imagesData, isLoading };
};
